/**
 * poolAuthority — the client's read of who may lock, unlock and generate
 * tracks, so a button is disabled (with the reason) BEFORE a click the server
 * would refuse. The server stays the wall; these mirror it:
 *
 *   lock     set_item_pool_lock (749)       any active member of the workspace
 *   unlock   set_item_pool_lock (749/761)   the person who locked it, the cycle
 *                                           owner, or a workspace admin
 *   generate materialize_baseline_tracks    internal_cycle_design_authority:
 *                                           the cycle owner (owner_id, else
 *                                           created_by) or a workspace admin
 *
 * One rule here is the client's own, not a mirror (owner ruling, 2026-11-02):
 *
 *   import ranks  a re-imported spreadsheet   a pool in a cycle (its own, or
 *                 changing the pool's order   its track's): the cycle owner
 *                                             only. A pool outside any cycle
 *                                             or track: whoever may reorder it.
 *
 * The server cannot tell an imported order from a dragged one; it still
 * decides whether the reorder itself is allowed.
 *
 * Pure: no stores, no React. Tested in poolAuthority.test.ts.
 */

export interface PoolActor {
  /** The signed-in person; null/'anonymous' means nobody is signed in. */
  userId: string | null | undefined;
  /** Workspace owner/admin (or platform admin). */
  isWorkspaceAdmin: boolean;
}

export interface CycleOwnership {
  ownerId?: string | null;
  createdBy?: string | null;
}

export interface GateDecision {
  allowed: boolean;
  /** Why not — shown as the disabled button's title. */
  reason?: string;
}

const same = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

function signedIn(userId: string | null | undefined): userId is string {
  return !!userId && userId !== 'anonymous';
}

/** Mirrors internal_cycle_design_authority: owner_id, else created_by when there is no owner. */
export function isCycleOwner(userId: string | null | undefined, cycle: CycleOwnership | null | undefined): boolean {
  if (!signedIn(userId) || !cycle) return false;
  if (cycle.ownerId) return same(cycle.ownerId, userId);
  return same(cycle.createdBy, userId);
}

export const SIGN_IN_REASON = 'Sign in to change this pool.';
export const UNLOCK_AUTHORITY_REASON =
  'Only the person who locked this pool, the cycle owner or a workspace admin can unlock it.';
export const GENERATE_AUTHORITY_REASON =
  'Only the cycle owner or a workspace admin can generate tracks for this cycle.';

export function canLockPool(actor: PoolActor): GateDecision {
  return signedIn(actor.userId) ? { allowed: true } : { allowed: false, reason: SIGN_IN_REASON };
}

export function canUnlockPool(
  actor: PoolActor,
  pool: { poolLockedBy?: string | null },
  cycle: CycleOwnership | null | undefined,
): GateDecision {
  if (!signedIn(actor.userId)) return { allowed: false, reason: SIGN_IN_REASON };
  if (actor.isWorkspaceAdmin || same(pool.poolLockedBy, actor.userId) || isCycleOwner(actor.userId, cycle)) {
    return { allowed: true };
  }
  return { allowed: false, reason: UNLOCK_AUTHORITY_REASON };
}

export function canGenerateTracks(actor: PoolActor, cycle: CycleOwnership | null | undefined): GateDecision {
  if (!signedIn(actor.userId)) return { allowed: false, reason: SIGN_IN_REASON };
  if (actor.isWorkspaceAdmin || isCycleOwner(actor.userId, cycle)) return { allowed: true };
  return { allowed: false, reason: GENERATE_AUTHORITY_REASON };
}

export const REORDER_RIGHT_REASON = 'You can’t reorder this pool, so ranks in the file were not applied.';

export interface PoolCycleScope {
  /** The pool's cycle, or its track's; null when the pool is in neither. */
  cycleId: string | null;
  /** That cycle's ownership, when it is loaded. */
  cycle: CycleOwnership | null | undefined;
  /** For the refusal: the cycle owner's name, when known. */
  ownerName?: string | null;
}

/** Why a re-import's ranks were not applied to a pool in a cycle. */
export function importRanksCycleOwnerReason(ownerName?: string | null): string {
  const owner = ownerName?.trim();
  return owner
    ? `This pool is part of a cycle, so only its cycle owner, ${owner}, can change its ranks by importing a file. The ranks in the file were not applied.`
    : 'This pool is part of a cycle, so only its cycle owner can change its ranks by importing a file. The ranks in the file were not applied.';
}

/**
 * May a re-imported spreadsheet change this pool's order? In a cycle, only
 * the cycle owner; outside one, whoever may already reorder the pool
 * (`canReorder`, the same right the drag handle asks).
 */
export function canImportPoolRanks(actor: PoolActor, scope: PoolCycleScope, canReorder: boolean): GateDecision {
  if (!signedIn(actor.userId)) return { allowed: false, reason: SIGN_IN_REASON };
  if (scope.cycleId) {
    return isCycleOwner(actor.userId, scope.cycle)
      ? { allowed: true }
      : { allowed: false, reason: importRanksCycleOwnerReason(scope.ownerName) };
  }
  return canReorder ? { allowed: true } : { allowed: false, reason: REORDER_RIGHT_REASON };
}

/** The Pool Amendment note shown on a locked pool (session_baselines.pool_amended_at, 723/747). */
export function describePoolAmendment(
  amendedAt: string | Date | null | undefined,
  note: string | null | undefined,
): string | null {
  if (!amendedAt) return null;
  const when = amendedAt instanceof Date ? amendedAt : new Date(amendedAt);
  if (Number.isNaN(when.getTime())) return null;
  const date = when.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  const reason = note?.trim();
  return reason
    ? `Amended after lock on ${date}: ${reason}. Sessions already under way on this track were started on the earlier membership.`
    : `Amended after lock on ${date}. Sessions already under way on this track were started on the earlier membership.`;
}
